import { useModal } from 'modals';

import HorizontalScroll from '@cartwhl/ui/atoms/HorizontalScroll';
import LazyVideo from '@cartwhl/ui/atoms/LazyVideo';
import Text from '@cartwhl/ui/base/Text';
import PlusIcon from '@cartwhl/ui/icons/PlusIcon';

import ContentButton from 'components/ContentButton';
import SectionHeader from 'components/SectionHeader';
import type { WorkflowKey } from 'components/WorkflowModal/index';
import { CDN } from 'consts';
import { useCarousel } from 'hooks/useCarousel';

import LogoCarousel, { type LogoItem } from './LogoCarousel';
import { VerticalNavButtons } from './VerticalNavButtons';
import {
  BitReactorLogo,
  DogstarLogo,
  FrameMachineLogo,
  GoogleLogo,
  LocksmithLogo,
  MrXLogo,
  PsyopLogo,
  WushuStudiosLogo,
} from './logos';

const workflows: {
  title: string;
  description: string;
  time: string;
  videoSrc: string;
  workflowKey: WorkflowKey;
  comingSoon?: boolean;
  comingSoonText?: string;
}[] = [
  {
    title: 'Block and refine a performance',
    description:
      'Capture a performance and iteratively shape timing, pose, and intent — without a stage.',
    time: '5 min workflow',
    videoSrc: `${CDN}workflow_1_step_4.webm`,
    workflowKey: 'capture',
  },
  {
    title: 'Bring human performance to creatures and stylized characters',
    description:
      'Apply human motion to non-human characters while preserving intent and timing.',
    time: '5 min workflow',
    videoSrc: `${CDN}v2_homepage_workflows_bring_human_performances_output.webm`,
    workflowKey: 'characters',
    comingSoon: true,
    comingSoonText: 'Closed beta',
  },
  {
    title: 'Animate a brand mascot for campaigns',
    description:
      'Create consistent, reusable motion for characters across campaigns, scenes, or platforms.',
    time: '5 min workflow',
    videoSrc: `${CDN}v2_homepage_workflows_01_mascot.webm`,
    workflowKey: 'mascot',
    comingSoon: true,
    comingSoonText: 'Closed beta',
  },
  {
    title: 'Generate animations for crowds',
    description:
      'Generate multiple variations of motions for generated characters to apply to crowds or NPCs.',
    time: '5 min',
    videoSrc: `${CDN}v2_homepage_workflows_02_crowds.webm`,
    workflowKey: 'crowds',
    comingSoon: true,
    comingSoonText: 'Closed beta',
  },
];

const trustedByLogos: LogoItem[] = [
  { name: 'Psyop', icon: <PsyopLogo />, width: 'w-[68px] lg:w-[92px]' },
  {
    name: 'Dogstar',
    icon: <DogstarLogo />,
    width: 'w-[84px] lg:w-[120px]',
  },
  {
    name: 'Google',
    icon: <GoogleLogo />,
    width: 'w-[82px] lg:w-[120px]',
  },
  {
    name: 'Bit Reactor',
    icon: <BitReactorLogo />,
    width: 'w-[92px] lg:w-[132px]',
  },
  {
    name: 'Frame Machine',
    icon: <FrameMachineLogo />,
    width: 'w-[40px] lg:w-[70px]',
  },

  {
    name: 'Locksmith',
    icon: <LocksmithLogo />,
    width: 'w-[72px] lg:w-[110px]',
  },
  {
    name: 'Mr X',
    icon: <MrXLogo />,
    width: 'w-[66px] lg:w-[92px]',
  },
  {
    name: 'Wushu Studios',
    icon: <WushuStudiosLogo />,
    width: 'w-[80px] lg:w-[140px]',
  },
];

export default function WorkflowsSection() {
  const { open } = useModal();
  const {
    scrollRef,
    firstItemRef,
    intersectionRef,
    canScrollPrev,
    canScrollNext,
    scrollPrev,
    scrollNext,
  } = useCarousel();

  return (
    <section ref={intersectionRef} className="py-(--vertical-spacing)">
      <div className="container">
        <SectionHeader
          title="Workflows"
          description="Integrate seamlessly into existing pipelines, delivering production-ready work in minutes—not months."
        />
      </div>

      {/* Carousel - extends beyond container */}
      <HorizontalScroll ref={scrollRef} as="section">
        {workflows.map((workflow, index) => {
          const isComingSoon = workflow.comingSoon ?? false;

          return (
            <div
              key={workflow.title}
              ref={index === 0 ? firstItemRef : undefined}
              className="-my-3 flex w-[329px] shrink-0 snap-start flex-col py-3 lg:w-[632px]"
            >
              <button
                className="hover:shadow-menu group relative mb-7 h-[184px] cursor-pointer overflow-hidden rounded-2xl transition-shadow  lg:mb-12 lg:h-[352px]"
                type="button"
                onClick={() => {
                  if (isComingSoon) {
                    open('NOTIFY_ME', {
                      excitedAbout: `Workflow: ${workflow.title}`,
                    });
                  } else {
                    open('WORKFLOW', { key: workflow.workflowKey });
                  }
                }}
              >
                <LazyVideo
                  src={workflow.videoSrc}
                  className="absolute inset-0 h-full w-full rounded-[inherit]"
                  mode="autoplay"
                  threshold={0}
                />
                <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(to_top,var(--color-gray-90)_-50%,transparent_50%)]" />
                <div className="absolute bottom-4 left-4 z-10 lg:bottom-8 lg:left-8">
                  <ContentButton
                    comingSoon={isComingSoon}
                    comingSoonText={workflow.comingSoonText}
                    excitedAbout={`Workflow: ${workflow.title}`}
                  >
                    <PlusIcon />
                  </ContentButton>
                </div>
              </button>

              <div className="flex flex-col gap-5">
                <Text
                  lg:variant="web/body/medium/xl"
                  variant="web/body/medium/lg"
                >
                  {workflow.title}
                </Text>
                <Text
                  lg:variant="web/body/medium/24"
                  variant="web/body/medium/md"
                  className="text-gray-45"
                >
                  {workflow.description}
                </Text>
                <Text
                  lg:variant="web/body/medium/24"
                  variant="web/body/medium/md"
                >
                  {workflow.time}
                </Text>
              </div>
            </div>
          );
        })}
      </HorizontalScroll>

      {/* Navigation */}
      <VerticalNavButtons
        disablePrev={!canScrollPrev}
        disableNext={!canScrollNext}
        onPrev={scrollPrev}
        onNext={scrollNext}
        className="container mt-8"
      />

      <LogoCarousel
        label="Trusted by:"
        logos={trustedByLogos}
        className="mt-12 lg:mt-20"
      />
    </section>
  );
}
